import { Grid, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useFetchAddress } from 'hooks/useFetchAddress';
import React, { ChangeEvent, KeyboardEvent, useRef, useState } from 'react';
import { GeonorgeAddress } from 'types/Geonorge/GeonorgeAddress';
import './DeliveriesConfigAddressAutocomplete.scss';

interface DeliveriesConfigAddressAutocompleteProps {
  text: string;
  lon: number;
  lat: number;
  draft: {
    endpoint?: {
      lon: number;
      lat: number;
    };
    endpointAddress?: string;
  };
  editable?: boolean;
  updatePinpointDraggable: (status: boolean) => void;
  onChangeDraft: (lon?: number, lat?: number, name?: string) => void;
}

export const DeliveriesConfigAddressAutocomplete = ({
  updatePinpointDraggable,
  onChangeDraft,
  ...props
}: DeliveriesConfigAddressAutocompleteProps) => {
  const [userInput, setUserInput] = useState('');

  /* Address Information */
  const [address, setAddress] = useState<string>(null);
  const [latitude, setLatitude] = useState<number>(null);
  const [longitude, setLongitude] = useState<number>(null);

  const inputRef = useRef<HTMLDivElement>();

  const fetchAddress = useFetchAddress();

  const addresses = fetchAddress.suggestion;

  const onUserInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const textInput = e?.target?.value || '';
    setUserInput(textInput);
    fetchAddress.search(textInput);
  };

  const onUserInputKeypress = (evt: KeyboardEvent<HTMLDivElement>) => {
    /* User pressed `Enter` */
    if (evt.keyCode === 13) {
      /* When there's no address showing on the options, */
      /* then we need to prevent the user to press `Enter` */
      const addressExist = addresses?.length > 0;
      if (!addressExist) {
        evt.preventDefault();
        evt.stopPropagation();
      }
    }

    /* We should not let the symbol key pressed */
    /* Using `evt.key` -> we can get the description of key pressed */
    /* When the description is symbol, we just ignore */
    const nonAlphanumericRegex = new RegExp(/[^0-9a-z\s]/gi);
    const isNonAlphanumeric = nonAlphanumericRegex.test(evt.key);
    if (isNonAlphanumeric) {
      evt.preventDefault();
      evt.stopPropagation();
    }
  };

  const onChangeAutocomplete = (
    _: ChangeEvent<any>,
    value: string | GeonorgeAddress
  ) => {
    if (!value) {
      return;
    }

    const selectedAddress = value as GeonorgeAddress;
    const addressInformationValid =
      selectedAddress.adressetekst &&
      selectedAddress.postnummer &&
      selectedAddress.poststed &&
      selectedAddress.representasjonspunkt.lat &&
      selectedAddress.representasjonspunkt.lon;

    /* Only do something when the Address is valid */
    if (addressInformationValid) {
      const newAddress = `${selectedAddress.adressetekst}, ${selectedAddress.postnummer}, ${selectedAddress.poststed}`;
      const newLatitude = selectedAddress.representasjonspunkt.lat;
      const newLongitude = selectedAddress.representasjonspunkt.lon;

      setAddress(newAddress);
      setLatitude(newLatitude);
      setLongitude(newLongitude);

      /* Reset the Draft changes from Pinpoint */
      onChangeDraft(newLongitude, newLatitude, newAddress);
    }
  };

  return (
    <div className="config-container">
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Grid item ref={inputRef}>
          <p className="label">Courier's endpoint</p>
          {props.editable && (
            <Autocomplete
              freeSolo
              autoHighlight /* Auto-select the first option to prevent error when user press `Enter` */
              id="deliveries-config-address"
              options={addresses}
              getOptionLabel={option => option.adressetekst || ''}
              renderOption={(props, option) => (
                <div>
                  <p>{props.adressetekst}</p>
                  <p>
                    <small>
                      {props.postnummer}, {props.poststed}
                    </small>
                  </p>
                </div>
              )}
              renderInput={params => (
                <TextField
                  autoFocus
                  {...params}
                  variant="outlined"
                  value={userInput}
                  onChange={onUserInputChange}
                  onKeyDown={onUserInputKeypress}
                />
              )}
              onChange={onChangeAutocomplete}
            />
          )}
          <div className="config-value">
            {props.draft ? (
              <>
                {props.draft.endpointAddress && (
                  <p>{props.draft.endpointAddress}</p>
                )}
                <p className="coordinate">
                  <small>LON {props.draft?.endpoint?.lon}</small>
                </p>
                <p className="coordinate">
                  <small>LAT {props.draft?.endpoint?.lat}</small>
                </p>
              </>
            ) : (
              <>
                {(props.lat && props.lon) || (longitude && latitude) ? (
                  <>
                    {(props.text || address) && <p>{address || props.text}</p>}
                    <p className="coordinate">
                      <small>LON {longitude || props.lon}</small>
                    </p>
                    <p className="coordinate">
                      <small>LAT {latitude || props.lat}</small>
                    </p>
                  </>
                ) : (
                  <p>No endpoint selected.</p>
                )}
              </>
            )}
          </div>
        </Grid>
      </Grid>
    </div>
  );
};
